import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import EditIcon from '@mui/icons-material/Edit';
import TableCellTooltip from '../../../common/TableCellTooltip';
import { focusElemnt, navigation3 } from '../../../common/helper';
import * as mdt from '../../../common/modaltypes';
import { getColumnDefs } from './BrnchMasterConstant';
import moment from 'moment/moment';
import { companySettingsAtom } from '../../../common/jotaiState/commonStateAtoms';
import { useAtom } from 'jotai';
import { useTranslation } from '../../../../i18n/I18nProvider';

const BranchMasterTable = ({ setSelEditBranch, updateModal, showModal, modalType, tableData, loginData, selectedBranchIds, setSelectedBranchIds, selectedStatus }) => {
    const { translations } = useTranslation()
    const bum = translations?.branchAndUserMaster || {}
    const cs = translations?.common || {}
    const originalColumnDefs = getColumnDefs(translations)
    const [reportData, setReportData] = useState(tableData);
    const [companySettings] = useAtom(companySettingsAtom)
    const [selModalRow, setSelModalRow] = useState('');
    const [searchData, setSearchData] = useState({
        CompanyName:"",
        BranchID: "",
        BranchCode: "",
        BranchPayBillNo: "",
        BranchName: "",
        CityName: "",
        StateName: "",
        BranchType: "",
        GSTN: "",
        CargoGSTNO: "",
        BranchManagerName: "",
        ContactNo1: "",
    });
    const { DefaultCurrencyID } = companySettings

    // Memoize column definitions
    const columnDefs = useMemo(() => {
        let cols =
            DefaultCurrencyID === 2
                ? originalColumnDefs.filter(
                    (col) =>
                        col.field !== 'GSTN' &&
                        col.field !== 'CargoGSTNO'
                )
                : [...originalColumnDefs];

        if (DefaultCurrencyID === 2) {
            const branchCodeIndex = cols.findIndex(
                (col) => col.field === 'BranchCode'
            );

            if (branchCodeIndex !== -1) {
                cols = [
                    ...cols.slice(0, branchCodeIndex + 1),
                    {
                        headerName: bum.branchPaybill || 'Branch Paybill',
                        field: 'BranchPayBillNo',
                        headerSx: {
                            textAlign: 'left',
                            minWidth: '130px',
                        },
                        showfilter: true,
                    },
                    ...cols.slice(branchCodeIndex + 1),
                ];
            }
        }

        if (selectedStatus !== 0) {
            return cols.filter(col => col.field !== 'Select');
        }
        return cols;
    }, [DefaultCurrencyID, selectedStatus, originalColumnDefs, bum.branchPaybill]);

    // Sort and filter table data
    const filterAndSortData = useCallback(() => {
        const filteredData = tableData.filter(val =>
            Object.keys(searchData).every(key =>
                searchData[key] === "" ||
                (val[key]?.toString().toLowerCase().includes(searchData[key].toString().toLowerCase()))
            )
        );
        return filteredData.sort((a, b) => a.BranchID - b.BranchID);
    }, [tableData, searchData]);

    useEffect(() => {
        setReportData(filterAndSortData());
    }, [filterAndSortData]);

    // Handle search data changes with debouncing
    const handleSearchData = (event, field) => {
        setSearchData(prevData => ({ ...prevData, [field]: event.target.value }));
    };

    const keyboardNavigation = (event, prevId, nextId) => {
        if (event.key === 'Escape') {
            focusElemnt(prevId);
        }
        if (event.key === 'Enter') {
            focusElemnt(nextId);
        }
    };

    const handleDeleteSelection = (branchID, isActive) => {
        if (isActive === 1) return;

        setSelectedBranchIds((prev) => (
            prev.includes(branchID)
                ? prev.filter((id) => id !== branchID)
                : [...prev, branchID]
        ));
    };
    const isAllSelected = reportData.length > 0 &&
        reportData.every(val =>
            val.IsActive === 1 || selectedBranchIds.includes(val.BranchID)
        );

    const isIndeterminate = reportData.some(val =>
        val.IsActive !== 1 && selectedBranchIds.includes(val.BranchID)
    ) && !isAllSelected;

    const handleSelectAll = (checked) => {
        if (checked) {
            const allIds = reportData
                .filter(val => val.IsActive !== 1) // only selectable
                .map(val => val.BranchID);

            setSelectedBranchIds(allIds);
        } else {
            setSelectedBranchIds([]);
        }
    };
    return (
        <TableContainer className='scrollBlue' component={Paper} sx={{ maxHeight: '70vh', marginBottom: "5px" }}>
            <Table stickyHeader size="small" aria-label="a dense table">
                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <TableRow>
                        {columnDefs.map((col, index) => (
                            <TableCell key={`TH-${index}`} sx={{ ...col.headerSx, backgroundColor: '#DAEDFE', padding: '8px 6px !important', minHeight: '36px !important', fontSize: '12px', fontWeight: '700', color: '#444' }}>
                                {col.headerName}
                            </TableCell>
                        ))}
                    </TableRow>
                    <TableRow sx={{ '& .MuiTableCell-root': { padding: '6px !important' } }}>
                        {columnDefs.map((col, index) => (
                            col.field === 'Select' ? (
                                <TableCell key={`FTH-${index}`}>
                                    <Checkbox
                                        checked={isAllSelected}
                                        indeterminate={isIndeterminate}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        sx={{ padding: 0 }}
                                    />
                                </TableCell>
                            ) : col.showfilter ? (
                                <TableCell key={`FTH-${index}`}>
                                    <div className="filter-input" style={{ fontSize: '12px', justifyContent: 'space-between' }}>
                                        <input
                                            id={`Contains-${index}`}
                                            className="field"
                                            value={searchData[col.field]}
                                            name="cancelSearchInput"
                                            style={{ width: '100%' }}
                                            placeholder={cs.contains || bum.contains || "Contains"}
                                            onChange={(e) => handleSearchData(e, col.field)}
                                            onKeyDown={(e) => keyboardNavigation(e, index === 1 ? "createBtn" : `Contains-${index - 1}`, index === 11 ? reportData.length > 0 ? `Edit-0` : 'status' : `Contains-${index + 1}`)}
                                        />
                                        <FilterAltIcon sx={{ paddingRight: '0px !important', color: '#aaa !important' }} />
                                    </div>
                                </TableCell>
                            ) : <TableCell key={`FTH-${index}`}></TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody sx={{ '& .MuiTableCell-root': { padding: '6px !important' } }}>
                    {reportData.map((val, index) => (
                        <TableRow key={`DR-${index}`} sx={{ backgroundColor: selModalRow === index && showModal && mdt.editBranchMaster === modalType ? '#FFFDAF99' : '' }}>
                            {selectedStatus === 0 && <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#666' }}>
                                <Checkbox
                                    checked={selectedBranchIds.includes(val.BranchID)}
                                    disabled={val.IsActive === 1}
                                    onChange={() => handleDeleteSelection(val.BranchID, val.IsActive)}
                                    sx={{ padding: 0 }}
                                />
                            </TableCell>}
                            <TableCell sx={{ minHeight: '36px', textAlign: 'right', fontSize: '12px', color: '#444' }}>{index + 1}</TableCell>
                            <TableCellTooltip title={val?.CompanyName} data={val?.CompanyName} placement={'bottom-start'} textAlign={"left"} maxWidth={'160px'} />
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.BranchID}</TableCell>
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.BranchCode}</TableCell>
                            {DefaultCurrencyID === 2 && (
                                <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.BranchPayBillNo}</TableCell>
                            )}
                            <TableCellTooltip title={val.BranchName} data={val.BranchName} placement={'bottom-start'} textAlign={"left"} maxWidth={'15%'} />
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.CityName}</TableCell>
                            <TableCellTooltip title={val.StateName} data={val.StateName} placement={'bottom-start'} textAlign={"left"} maxWidth={'90px'} />
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.BranchType}</TableCell>
                            {DefaultCurrencyID !== 2 && (
                                <>
                                    <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.GSTN}</TableCell>
                                    <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.CargoGSTNO}</TableCell>
                                </>
                            )}
                            <TableCellTooltip title={val.BranchManagerName} data={val.BranchManagerName} placement={'bottom-start'} textAlign={"left"} maxWidth={'100px'} />
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.ContactNo1}</TableCell>
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.CreatedDatetime === "" ? '' : moment(val.CreatedDatetime).format('DD MMM YYYY,HH:mm')}</TableCell>
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.ModifiedDatetime === "" ? '' : moment(val.ModifiedDatetime).format('DD MMM YYYY,HH:mm')}</TableCell>
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#444' }}>{val.ModifiedByUser}</TableCell>
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#666' }}>
                                <Checkbox checked={val.IsActive === 1} sx={{ padding: 0 }} />
                            </TableCell>
                            <TableCell sx={{ minHeight: '36px', textAlign: 'left', fontSize: '12px', color: '#666', cursor: "pointer" }}>
                                <span
                                    id={`Edit-${index}`} tabIndex={0}
                                    style={{ display: 'flex', color: '#007bfd', fontSize: '13px', fontWeight: 'bold' }}
                                    onClick={() => {
                                        setSelModalRow(index);
                                        setSelEditBranch(val.BranchID);
                                        updateModal(true, mdt.editBranchMaster);
                                    }}
                                    onKeyDown={(e) => {
                                        navigation3(e, reportData.length - 1 === index ? 'status' : `Edit-${index + 1}`, index === 0 ? 'Contains-11' : `Edit-${index - 1}`, () => { });
                                    }}
                                >
                                    <EditIcon sx={{ marginRight: '3px' }} />
                                    {bum.edit || 'Edit'}
                                </span>
                            </TableCell>

                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default BranchMasterTable;
